import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Upload, PlayCircle, QrCode, Check, X, ChevronRight, ChevronLeft, Trash2, Image, Film } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const STEPS = [
  { id: 'screen', icon: Monitor },
  { id: 'media', icon: Upload },
  { id: 'playlist', icon: PlayCircle },
  { id: 'connect', icon: QrCode }
];

const SetupWizard = ({ onComplete, onSkip, token }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    screen: { name: '', location: '' },
    media: [],
    playlist: { name: 'Meine erste Playlist', duration: 10 },
    createdScreen: null,
    connectionPin: null,
    createdPlaylist: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // ================================================
  // FILE UPLOAD HANDLERS
  // ================================================
  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const maxSize = 50 * 1024 * 1024; // 50MB
      return (isImage || isVideo) && file.size <= maxSize;
    });

    if (validFiles.length === 0) {
      setError('Bitte wähle gültige Bild- oder Videodateien (max. 50MB)');
      return;
    }

    setError(null);

    // Upload each file
    for (const file of validFiles) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    const tempId = Date.now() + Math.random();
    
    // Add to uploading state
    setUploadingFiles(prev => [...prev, { id: tempId, name: file.name, progress: 0 }]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('duration', '10');

      const response = await fetch(`${API_URL}/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const uploadedMedia = await response.json();
        console.log('📁 Media uploaded:', uploadedMedia);

        // Add to media list
        setWizardData(prev => ({
          ...prev,
          media: [...prev.media, uploadedMedia]
        }));
      } else {
        const err = await response.json();
        setError(err.error || 'Upload fehlgeschlagen');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload fehlgeschlagen: ' + err.message);
    } finally {
      // Remove from uploading state
      setUploadingFiles(prev => prev.filter(f => f.id !== tempId));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const removeMedia = async (mediaId) => {
    try {
      await fetch(`${API_URL}/media/${mediaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWizardData(prev => ({
        ...prev,
        media: prev.media.filter(m => m.id !== mediaId)
      }));
    } catch (err) {
      console.error('Error deleting media:', err);
    }
  };

  // ================================================
  // STEP NAVIGATION
  // ================================================
  const handleNext = async () => {
    setError(null);

    if (currentStep === 0) {
      // Step 1: Create Screen
      if (!wizardData.screen.name.trim()) return;

      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/screens`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: wizardData.screen.name,
            location: wizardData.screen.location,
            connectionType: 'managed_device'
          })
        });

        if (response.ok) {
          const data = await response.json();
          const screen = data.screen || data;
          const pin = data.connectionInfo?.registrationToken ||
                      screen.registration_token ||
                      data.registration_token;

          setWizardData(prev => ({
            ...prev,
            createdScreen: screen,
            connectionPin: pin
          }));
          setCurrentStep(prev => prev + 1);
        } else {
          const err = await response.json();
          setError(err.error || 'Fehler beim Erstellen des Bildschirms');
        }
      } catch (err) {
        console.error('Error creating screen:', err);
        setError('Netzwerkfehler. Bitte versuche es erneut.');
      } finally {
        setIsLoading(false);
      }
    } else if (currentStep === 1) {
      // Step 2: Media - just move to next step
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === 2) {
      // Step 3: Create Playlist if media exists
      if (wizardData.media.length > 0) {
        setIsLoading(true);
        try {
          // ✅ FIXED: media_ids als Integer-Array
          const mediaIds = wizardData.media.map(m => parseInt(m.id));
          
          console.log('📋 Creating playlist with media_ids:', mediaIds);
          
          const playlistResponse = await fetch(`${API_URL}/playlists`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              name: wizardData.playlist.name || 'Meine erste Playlist',
              description: '',
              loop: true,
              media_ids: mediaIds
            })
          });

          if (playlistResponse.ok) {
            const playlist = await playlistResponse.json();
            console.log('✅ Playlist created:', playlist);

            // ✅ FIXED: Assign playlist to screen with POST and playlist_id (snake_case!)
            if (wizardData.createdScreen?.id) {
              console.log('🔗 Assigning playlist to screen:', wizardData.createdScreen.id);
              
              const assignResponse = await fetch(`${API_URL}/screens/${wizardData.createdScreen.id}/playlist`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  playlist_id: playlist.id,  // ✅ snake_case for validation!
                  playlistId: playlist.id    // ✅ camelCase for handler!
                })
              });
              
              if (assignResponse.ok) {
                console.log('✅ Playlist assigned to screen');
              } else {
                const errText = await assignResponse.text();
                console.error('❌ Failed to assign playlist:', errText);
              }
            }

            setWizardData(prev => ({
              ...prev,
              createdPlaylist: playlist
            }));
          } else {
            const errData = await playlistResponse.json();
            console.error('❌ Playlist creation failed:', errData);
            setError(errData.error || 'Playlist konnte nicht erstellt werden');
          }
        } catch (err) {
          console.error('Error creating playlist:', err);
          setError('Fehler beim Erstellen der Playlist');
        } finally {
          setIsLoading(false);
        }
      }
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === STEPS.length - 1) {
      // Complete wizard
      await completeOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const completeOnboarding = async () => {
    try {
      await fetch(`${API_URL}/auth/onboarding-complete`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      onComplete();
    } catch (err) {
      console.error('Error completing onboarding:', err);
      onComplete();
    }
  };

  const handleSkip = async () => {
    await completeOnboarding();
    onSkip();
  };

  const StepIcon = STEPS[currentStep].icon;

  // ================================================
  // RENDER
  // ================================================
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white flex-shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{t('wizard.title')}</h2>
              <p className="text-primary-100 mt-1">{t('wizard.subtitle')}</p>
            </div>
            <button
              onClick={handleSkip}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title={t('wizard.skipAll')}
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <React.Fragment key={step.id}>
                  <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full transition-all
                    ${isActive ? 'bg-white text-primary-600 scale-110' : ''}
                    ${isCompleted ? 'bg-primary-400 text-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-primary-500/50 text-primary-200' : ''}
                  `}>
                    {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${
                      index < currentStep ? 'bg-primary-400' : 'bg-primary-500/50'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
              <StepIcon className="text-primary-600 dark:text-primary-400" size={32} />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {t(`wizard.step${currentStep + 1}.title`)}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(`wizard.step${currentStep + 1}.description`)}
            </p>
          </div>

          {/* Step 1: Screen Name */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('wizard.step1.nameLabel')} *
                </label>
                <input
                  type="text"
                  value={wizardData.screen.name}
                  onChange={(e) => setWizardData(prev => ({
                    ...prev,
                    screen: { ...prev.screen, name: e.target.value }
                  }))}
                  placeholder={t('wizard.step1.namePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('wizard.step1.locationLabel')}
                </label>
                <input
                  type="text"
                  value={wizardData.screen.location}
                  onChange={(e) => setWizardData(prev => ({
                    ...prev,
                    screen: { ...prev.screen, location: e.target.value }
                  }))}
                  placeholder={t('wizard.step1.locationPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          )}

          {/* Step 2: Media Upload */}
          {currentStep === 1 && (
            <div className="space-y-4">
              {/* Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
                  ${isDragOver
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
                  }
                `}
              >
                <Upload size={40} className={`mx-auto mb-3 ${isDragOver ? 'text-primary-500' : 'text-gray-400'}`} />
                <p className="text-gray-600 dark:text-gray-400 mb-1">{t('wizard.step2.dragDrop')}</p>
                <p className="text-xs text-gray-500">{t('wizard.step2.formats')}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
              </div>

              {/* Uploading Files */}
              {uploadingFiles.length > 0 && (
                <div className="space-y-2">
                  {uploadingFiles.map(file => (
                    <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Uploaded Media Grid */}
              {wizardData.media.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('wizard.step2.uploaded', { count: wizardData.media.length })}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {wizardData.media.map(media => (
  <div key={media.id} className="relative group aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
    {media.type === 'image' ? (
      <img
        src={media.url?.startsWith('http') ? media.url : `${API_URL.replace('/api', '')}${media.url}`}
        alt={media.name}
        className="w-full h-full object-cover"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center">
        <Film size={24} className="text-gray-400" />
      </div>
    )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMedia(media.id);
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={12} />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                          <p className="text-xs text-white truncate">{media.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {wizardData.media.length === 0 && uploadingFiles.length === 0 && (
                <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                  Du kannst diesen Schritt überspringen und später Medien hochladen.
                </p>
              )}
            </div>
          )}

          {/* Step 3: Playlist */}
          {currentStep === 2 && (
            <div className="space-y-4">
              {wizardData.media.length > 0 ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('wizard.step3.playlistName')}
                    </label>
                    <input
                      type="text"
                      value={wizardData.playlist.name}
                      onChange={(e) => setWizardData(prev => ({
                        ...prev,
                        playlist: { ...prev.playlist, name: e.target.value }
                      }))}
                      placeholder={t('wizard.step3.playlistPlaceholder')}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      ✅ {wizardData.media.length} {wizardData.media.length === 1 ? 'Medium wird' : 'Medien werden'} zur Playlist hinzugefügt und dem Bildschirm "{wizardData.screen.name}" zugewiesen.
                    </p>
                  </div>

                  {/* Preview of media in playlist */}
                  <div className="flex gap-2 overflow-x-auto py-2">
                    {wizardData.media.map((media, index) => (
                      <div key={media.id} className="flex-shrink-0 w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden relative">
                        {media.type === 'image' ? (
  <img 
    src={media.url?.startsWith('http') ? media.url : `${API_URL.replace('/api', '')}${media.url}`} 
    alt="" 
    className="w-full h-full object-cover" 
  />
) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film size={20} className="text-gray-400" />
                          </div>
                        )}
                        <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <PlayCircle size={40} className="mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Keine Medien hochgeladen. Du kannst später Playlists erstellen.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Connection PIN */}
          {currentStep === 3 && (
            <div className="text-center">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 mb-4 shadow-inner">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">{t('wizard.step4.pin')}</p>
                <p className="text-5xl font-mono font-bold text-primary-600 dark:text-primary-400 tracking-[0.3em]">
                  {wizardData.connectionPin || '----'}
                </p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('wizard.step4.orUsePin')}{' '}
                
                  <a href="https://connect.alpenscreen.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 font-semibold hover:underline"
                >
                  connect.alpenscreen.ch
                </a>
              </p>

              {/* Summary */}
              {(wizardData.media.length > 0 || wizardData.createdPlaylist) && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-left">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">✅ Setup abgeschlossen:</p>
                  <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                    <li>• Bildschirm "{wizardData.screen.name}" erstellt</li>
                    {wizardData.media.length > 0 && <li>• {wizardData.media.length} Medien hochgeladen</li>}
                    {wizardData.createdPlaylist && <li>• Playlist "{wizardData.playlist.name}" erstellt & zugewiesen</li>}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Step Counter */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            {t('wizard.step', { current: currentStep + 1, total: STEPS.length })}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex-shrink-0">
          {/* Buttons */}
          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50
                         dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <ChevronLeft size={18} />
                {t('wizard.back')}
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={(currentStep === 0 && !wizardData.screen.name.trim()) || isLoading}
              className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg
                       hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors flex items-center justify-center gap-2 font-medium"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {currentStep === STEPS.length - 1 ? t('wizard.finish') : t('wizard.next')}
                  {currentStep < STEPS.length - 1 && <ChevronRight size={18} />}
                </>
              )}
            </button>
          </div>

          {/* Skip Link */}
          <button
            onClick={handleSkip}
            className="w-full mt-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {t('wizard.skipAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;